import React, { useState } from 'react';
import { Settings, X } from 'lucide-react';

// --- Helper Functions ---

// Calculates the running total of an expression as an array of numbers/operators
const calculateRunningTotal = (expression) => {
  if (!Array.isArray(expression) || expression.length === 0) return null;
  let total = parseFloat(expression[0]);
  let result = [total];
  for (let i = 1; i < expression.length; i += 2) {
    const operator = expression[i];
    const next = parseFloat(expression[i + 1]);
    if (isNaN(next)) break;
    switch (operator) {
      case '+':
        total += next;
        break;
      case '-':
        total -= next;
        break;
      case '*':
      case '×':
        total *= next;
        break;
      case '/':
      case '÷':
        total /= next;
        break;
      default:
        break;
    }
    result.push(total);
  }
  return result;
};

// Evaluates an expression strictly left-to-right, ignoring operator precedence
const evaluateLeftToRight = (expr) => {
  if (!Array.isArray(expr) || expr.length === 0) return null;
  let total = parseFloat(expr[0]);
  for (let i = 1; i < expr.length; i += 2) {
    const operator = expr[i];
    const next = parseFloat(expr[i + 1]);
    if (isNaN(next)) break;
    switch (operator) {
      case '+':
        total += next;
        break;
      case '-':
        total -= next;
        break;
      case '*':
      case '×':
        total *= next;
        break;
      case '/':
      case '÷':
        total /= next;
        break;
      default:
        break;
    }
  }
  return total;
};

// Checks if two equations are commutatively equivalent
const checkCommutativeEquation = (guess, target) => {
  // FIXED: Add null/undefined checks before calling .replace()
  if (!guess || !target) return false;
  
  // Normalize: remove whitespace, sort numbers if possible, check equality
  const normalize = (eq) => (eq || '').replace(/\s+/g, '');
  if (normalize(guess) === normalize(target)) return true;

  // Try to compare sorted numbers/operators for simple commutative cases
  const split = (eq) => (eq || '').replace(/\s+/g, '').split(/([+\-*×/÷])/).filter(Boolean);
  const arr1 = split(guess);
  const arr2 = split(target);

  // If lengths differ, can't be commutative
  if (arr1.length !== arr2.length) return false;

  // For equations with only + and *, compare sorted terms
  const isOnlyCommutative = arr1.every((v) => ['+', '*', '×'].includes(v) || !isNaN(v));
  if (isOnlyCommutative) {
    const nums1 = arr1.filter(v => !isNaN(v)).sort();
    const nums2 = arr2.filter(v => !isNaN(v)).sort();
    const ops1 = arr1.filter(v => isNaN(v)).sort();
    const ops2 = arr2.filter(v => isNaN(v)).sort();
    return (
      JSON.stringify(nums1) === JSON.stringify(nums2) &&
      JSON.stringify(ops1) === JSON.stringify(ops2)
    );
  }

  // Otherwise fallback to strict equality
  return false;
};

// --- Main Component ---
function GameHeaderWithSettings({ runningTotal, gameOver, targetEquation, currentGuess }) {
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    allowCommutative: true,
    showRunningTotal: true,
    showModelAnswers: true,
    calculationOrder: 'standard',
    difficulty: 'normal'
  });

  // Example for commutative checking (replace with your actual logic)
  const isCorrectGuess = settings.allowCommutative
    ? checkCommutativeEquation(currentGuess, targetEquation)
    : currentGuess === targetEquation;

  return (
    <div>
      {/* Header Row with All Elements in Line */}
        <div className="flex items-center gap-3">
          {/* Running Total Display (inline) */}
          {settings.showRunningTotal && runningTotal !== null && (
            <div className="px-3 py-1 bg-blue-100 rounded">
              <span className="text-sm font-medium text-blue-700">{runningTotal}</span>
            </div>
          )}
          
          {/* Settings Button */}
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            aria-label="Settings"
          >
            <Settings size={20} />
          </button>
        </div>

      {/* Game Over Section with Model Answer */}
      {gameOver && settings.showModelAnswers && (
        <div className="p-3 bg-yellow-100 rounded">
          <p className="text-sm text-yellow-800">Correct answer: {targetEquation}</p>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg relative">
            <button
              className="absolute top-3 right-3 p-2"
              onClick={() => setShowSettings(false)}
            >
              <X size={20} />
            </button>
            <h2 className="text-xl font-bold mb-4">Game Settings</h2>
            <label className="flex items-center mb-2">
              <input
                type="checkbox"
                checked={settings.allowCommutative}
                onChange={e => setSettings(s => ({ ...s, allowCommutative: e.target.checked }))}
              />
              <span className="ml-2">Allow Commutative Checking</span>
            </label>
            <label className="flex items-center mb-2">
              <input
                type="checkbox"
                checked={settings.showRunningTotal}
                onChange={e => setSettings(s => ({ ...s, showRunningTotal: e.target.checked }))}
              />
              <span className="ml-2">Show Running Total</span>
            </label>
            <label className="flex items-center mb-2">
              <input
                type="checkbox"
                checked={settings.showModelAnswers}
                onChange={e => setSettings(s => ({ ...s, showModelAnswers: e.target.checked }))}
              />
              <span className="ml-2">Show Model Answers</span>
            </label>
            <div className="mb-2">
              <label className="block mb-1 font-medium">Calculation Order</label>
              <select
                className="w-full border rounded p-1"
                value={settings.calculationOrder}
                onChange={e => setSettings(s => ({ ...s, calculationOrder: e.target.value }))}
              >
                <option value="standard">Standard</option>
                <option value="left-to-right">Left-to-Right</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 font-medium">Difficulty</label>
              <select
                className="w-full border rounded p-1"
                value={settings.difficulty}
                onChange={e => setSettings(s => ({ ...s, difficulty: e.target.value }))}
              >
                <option value="normal">Normal</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Example usage for commutative checking */}
      {/* <div>
        {isCorrectGuess ? "Your guess matches (commutative check passed)!" : "Try again!"}
      </div> */}
    </div>
  );
}

export default GameHeaderWithSettings;
